import { Button, Flex, useMantineTheme } from "@mantine/core";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SegmentControlRounded, Select } from "../../../components";
import MultiSelectProducts from "../components/MultiSelectProducts";
import FilterClient from "../components/FilterClient";
import { optionsTwo } from "../mock";
import { useQueryClient } from "@tanstack/react-query";
import { useMediaQuery } from "@mantine/hooks";
import { usePosition } from "../../../hooks/usePositions";
import { IQueryPosition } from "../../../interfaces/IQuery";
import { useAuth } from "../../../context/auth";
import { ERole } from "../../../interfaces/User/newUser";
import GraphicPosition from "./GraphicPosition";
import {
  countdownDate,
  endDateToApiString,
  startDateToApiString,
} from "../../../utils/date";
import { useTranslation } from "react-i18next";
import HeadingOperation from "../components/HeadingOperation";
import ModalRangeDate from "../components/ModalRangeDate";

export default function ResumePosition() {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const isLargerThan780 = useMediaQuery("(min-width: 780px)");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("productId");
  const queryClient = useQueryClient();
  const { roleSelected, user } = useAuth();
  const userCreatedAt = user?.created_at;

  function dataRole() {
    return [
      {
        name: ERole.analyst,
        days: 30 - 1,
        isSelected: roleSelected === ERole.analyst,
      },
      {
        name: ERole.client,
        days: countdownDate(userCreatedAt),
        isSelected: roleSelected === ERole.client,
      },
    ];
  }

  const dataRoleSelected =
    dataRole().find((d) => d.isSelected) || dataRole()[1];

  const defaultQuery: IQueryPosition = {
    page: 1,
    pageSize: 10,
    open_date_start: new Date(
      new Date().setDate(new Date().getDate() - dataRoleSelected.days)
    ),
    open_date_end: new Date(),
    roleSelected: roleSelected,
    productId: !!id ? [Number(id)] : undefined,
  };

  const [query, setQuery] = useState<IQueryPosition>({
    ...defaultQuery,
    productId: !!id ? [Number(id)] : undefined,
  });

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,
      roleSelected: roleSelected,
      open_date_start: new Date(
        new Date().setDate(new Date().getDate() - countdownDate(userCreatedAt))
      ),
    }));
  }, [roleSelected]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    Object.entries(query).forEach(([key, value]) => {
      if (!value) return;
      if (key === "open_date_start") {
        const str = startDateToApiString(value as Date);
        if (str) newSearchParams.set(key, str);
      } else if (key === "open_date_end") {
        const str = endDateToApiString(value as Date);
        if (str) newSearchParams.set(key, str);
      } else if (Array.isArray(value)) {
        newSearchParams.set(key, value.join(","));
      } else {
        newSearchParams.set(key, String(value));
      }
    });

    navigate(`/operacoes/resumo-das-posicoes?${newSearchParams.toString()}`, {
      replace: true,
    });

    if (searchParams.size === 0) {
      navigate(`/operacoes/resumo-das-posicoes?${newSearchParams.toString()}`, {
        replace: true,
      });
    }
  }, [query, navigate, searchParams]);

  const { data: dataOrderPosition } = usePosition(query);
  // if (isError && isFetched && !data) return <PageNotFound />;

  const handleUpdate = () => {
    queryClient.refetchQueries({
      queryKey: [
        "position",
        query.order_status,
        query.order_side,
        query.is_open,
        query.page,
        query.pageSize,
        query.productId,
        query.orderBy,
        query.sortOrder,
        query.open_date_start,
        query.open_date_end,
        query.client_id,
      ],
    });
  };

  const handleSelectIsOpen = (value: string) => {
    setQuery((prevQuery) => ({ ...prevQuery, is_open: value }));
  };

  const handleClearFilters = () => {
    setQuery((prev) => ({ ...defaultQuery, productId: prev.productId }));
  };

  return (
    <Flex
      justify="space-between"
      align="center"
      bg="basic.0"
      p={{ base: "12px", 991: 32 }}
      direction={"column"}
      gap="24px"
      wrap={{ base: "wrap", 1650: "nowrap" }}
      sx={{ borderRadius: "12px 12px 0 0" }}
    >
      <Flex
        w="100%"
        gap={24}
        direction={{ base: "column", 1700: "column" }}
        justify={"space-between"}
        align={"start"}
      >
        <Flex
          w="100%"
          gap={24}
          direction={{ base: "column", 780: "row" }}
          justify={"space-between"}
          align={"start"}
        >
          <HeadingOperation
            lastUpdated={dataOrderPosition?.lastUpdated}
            title={t("operacoes.resumo_das_posicoes.title")}
            handleUpdate={handleUpdate}
          />
        </Flex>
        <Flex
          gap={24}
          wrap={{ base: "wrap", 1250: "nowrap" }}
          justify={{ base: "space-between", 1650: "flex-end" }}
          align={"end"}
          w="100%"
        >
          <ModalRangeDate query={query} setQuery={setQuery} />
          {isLargerThan780 ? (
            <SegmentControlRounded
              data={optionsTwo}
              value={query.is_open}
              label={t("operacoes.posicoes.position_status")}
              onChange={handleSelectIsOpen}
            />
          ) : (
            <Select
              data={optionsTwo}
              value={query.is_open || ""}
              placeholder={t("operacoes.posicoes.position_status")}
              label={t("operacoes.posicoes.position_status")}
              onChange={handleSelectIsOpen}
              w="100%"
            />
          )}
          <MultiSelectProducts query={query} setQuery={setQuery} />
          {roleSelected === ERole.analyst ? (
            <FilterClient query={query} setQuery={setQuery} />
          ) : null}
          <Button
            variant="primaryTransparent"
            w="100%"
            maw={{ base: "100%", 780: "fit-content" }}
            onClick={handleClearFilters}
            loaderProps={{ color: theme.colors.primary[1] }}
            h="40px"
          >
            {t("global.button.clean_filters")}
          </Button>
        </Flex>
      </Flex>
      <Flex w="100%" direction={"column"} gap={16}>
        <GraphicPosition query={query} />
      </Flex>
    </Flex>
  );
}
